/**
 * API CONFIGURATION
 * Central configuration for all backend API endpoints
 */

const getApiBaseUrl = () => {
  // Environment variable takes priority (for easy switching)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // ALWAYS use Render backend (removed localhost logic)
  return 'https://stericare-dashboard-v2-1.onrender.com';
};

export const API_BASE_URL = getApiBaseUrl();

// API Endpoints
export const API_ENDPOINTS = {
  HEALTH: `${API_BASE_URL}/health`,
  INFO: `${API_BASE_URL}/`,
  INVOICE_PDF: `${API_BASE_URL}/api/invoices/pdf`,
  INVOICE_EMAIL: `${API_BASE_URL}/api/invoices/email`,
  CREATE_USER: `${API_BASE_URL}/api/admin/users`,
};

// Check backend health
export const checkBackendHealth = async () => {
  try {
    console.log('🔍 Checking backend at:', API_ENDPOINTS.HEALTH);
    const response = await fetch(API_ENDPOINTS.HEALTH, { 
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    
    console.log('📡 Response status:', response.status);
    
    if (!response.ok) {
      throw new Error(`Backend responded with status ${response.status}`);
    }
    
    const data = await response.json();
    console.log('✅ Backend health check passed:', data);
    return { success: true, data };
  } catch (error) {
    console.error('❌ Backend health check failed:', error);
    console.error('Error details:', {
      name: error.name,
      message: error.message,
      url: API_ENDPOINTS.HEALTH
    });
    return { success: false, error: error.message };
  }
};

// Wake up backend (Render free-tier cold start)
export const wakeUpBackend = async (maxRetries = 3, retryDelay = 2000) => {
  console.log('🔄 Waking up backend server...');
  console.log('Target URL:', API_ENDPOINTS.HEALTH);
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`⏳ Attempt ${attempt}/${maxRetries}...`);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      const response = await fetch(API_ENDPOINTS.HEALTH, {
        signal: controller.signal,
        headers: { 
          'Cache-Control': 'no-cache', 
          'Pragma': 'no-cache' 
        },
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Backend is awake!', data);
        return { success: true, data, attempts: attempt };
      } else {
        console.log(`⚠️ Response status: ${response.status}`);
      }
    } catch (error) {
      console.log(`❌ Attempt ${attempt} failed:`, error.message);
      if (attempt < maxRetries) {
        console.log(`⏸️ Waiting ${retryDelay / 1000}s before retry...`);
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
      }
    }
  }
  
  console.error('❌ Failed to wake up backend after', maxRetries, 'attempts');
  return { 
    success: false, 
    error: 'Backend did not respond',
    message: 'Backend server is taking too long to start. It may be sleeping on Render free tier.'
  };
};

// Generate PDF
export const generateInvoicePDF = async (invoiceId) => {
  try {
    console.log('📄 Generating PDF for invoice:', invoiceId);
    
    const response = await fetch(API_ENDPOINTS.INVOICE_PDF, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invoiceId }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'PDF generation failed');
    }
    
    const blob = await response.blob();
    console.log('✅ PDF generated successfully');
    return { success: true, blob };
  } catch (error) {
    console.error('❌ PDF generation error:', error);
    return { 
      success: false, 
      error: error.message,
      message: 'Failed to generate PDF. Make sure backend server is running.'
    };
  }
};

// Download blob as file
export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

// Create user
export const createUser = async (userData) => {
  try {
    console.log('👤 Creating user:', userData.email);
    
    const response = await fetch(API_ENDPOINTS.CREATE_USER, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || data.error || 'User creation failed');
    }
    
    console.log('✅ User created successfully');
    return { success: true, data };
  } catch (error) {
    console.error('❌ User creation error:', error);
    return { 
      success: false, 
      error: error.message,
      message: 'Failed to create user. Make sure backend server is running.'
    };
  }
};

// Log configuration on load
console.log('🔧 API Configuration loaded');
console.log('📍 Backend URL:', API_BASE_URL);
console.log('🏥 Health endpoint:', API_ENDPOINTS.HEALTH);

export default {
  API_BASE_URL,
  API_ENDPOINTS,
  checkBackendHealth,
  wakeUpBackend,
  generateInvoicePDF,
  downloadBlob,
  createUser,
};
